import { useTranslation } from "react-i18next";
import {
    Autocomplete,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { Button, FoodsTags } from "@foodpilot/foods";
import { PuckPage } from "src/api/client-api/cms/useCreatePuckPage";
import { CompanySector } from "src/api/client-api/companySectors/useSupplierSectors";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useRoles } from "src/api/client-api/roles/useRoles";
import { Score } from "src/types";
import { cmsAutocompleteEnterSelectProps } from "src/utils/autocomplete";
import { scoreHasBaseline } from "src/utils/actionPlanScoreFilters";

export type PageSettingsDialogProps = {
    open: boolean;
    page?: PuckPage;
    pageType?: "CMS" | "PDF";
    settingsTitle: string;
    settingsDescription: string;
    settingsUrl: string;
    settingsRoles: string[];
    settingsActive: boolean;
    settingsSectorIds: number[];
    settingsScore?: string[];
    settingsEntity?: string[];
    settingsDownloadOnlyOnSsqValidated?: boolean;
    allSectors: CompanySector[];
    sectorOptions?: CompanySector[];
    selectedSectorOptions?: CompanySector[];
    onSectorInputChange?: (value: string) => void;
    scores?: Score[];
    disabled?: boolean;
    showStatusToggle?: boolean;
    urlError?: boolean;
    urlHelperText?: string;
    saveDisabled?: boolean;
    saveButtonLabel?: string;
    onChangeTitle: (v: string) => void;
    onChangeDescription: (v: string) => void;
    onChangeUrl: (v: string) => void;
    onChangeActive: (v: boolean) => void;
    onChangeRoles: (roles: string[]) => void;
    onChangeSectorIds: (ids: number[]) => void;
    onChangeScore?: (v: string[]) => void;
    onChangeEntity?: (v: string[]) => void;
    onChangeDownloadOnlyOnSsqValidated?: (value: boolean) => void;
    onClose: () => void;
    onSave: () => void;
    saving?: boolean;
};

export const PageSettingsDialog = (props: PageSettingsDialogProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const foodpilotContext = useFoodpilotContext();
    const { roles, rolesLoading, getRoleDisplayName } = useRoles();
    const {
        open,
        page,
        pageType = page?.pageType ?? "CMS",
        settingsTitle,
        settingsDescription,
        settingsUrl,
        settingsRoles,
        settingsActive,
        settingsSectorIds,
        settingsScore = [],
        settingsEntity = [],
        settingsDownloadOnlyOnSsqValidated = true,
        allSectors,
        sectorOptions,
        selectedSectorOptions,
        onSectorInputChange,
        scores = foodpilotContext?.getScoresByType ?
            foodpilotContext.getScoresByType("company")
        :   (foodpilotContext?.scores ?? []),
        disabled = false,
        showStatusToggle = Boolean(page),
        urlError = false,
        urlHelperText = "",
        saveDisabled = false,
        saveButtonLabel,
        onChangeTitle,
        onChangeDescription,
        onChangeUrl,
        onChangeActive,
        onChangeRoles,
        onChangeSectorIds,
        onChangeScore,
        onChangeEntity,
        onChangeDownloadOnlyOnSsqValidated,
        onClose,
        onSave,
        saving = false,
    } = props;

    const isPdfTemplate = pageType === "PDF";

    const resolvedSectorOptions = sectorOptions ?? allSectors;
    const resolvedSelectedSectorOptions =
        selectedSectorOptions ?? resolvedSectorOptions.filter((s) => settingsSectorIds.includes(s.id));

    const scoreOptions = [
        { label: t("common.all", "All"), value: "" },
        ...scores.filter(scoreHasBaseline).map((s) => ({ label: s.title, value: String(s.id) })),
    ];

    const entityOptions = [
        { label: t("common.all", "All"), value: "" },
        { label: t("entity.company", "Company"), value: "company" },
        { label: t("entity.product", "Product"), value: "product" },
        { label: t("entity.ingredient", "Ingredient"), value: "ingredient" },
        { label: t("entity.packaging", "Packaging"), value: "packaging" },
        { label: t("entity.supplier", "Supplier"), value: "supplier" },
        { label: t("entity.product_line", "Product line"), value: "productline" },
    ];

    const selectedScoreOptions = scoreOptions.filter((o) => settingsScore.includes(o.value));
    const selectedEntityOptions = entityOptions.filter((o) => settingsEntity.includes(o.value));

    const hasScoreSelection = selectedScoreOptions.length > 0;
    const hasEntitySelection = selectedEntityOptions.length > 0;

    const roleOptions = (roles ?? []).map((role) => role.code);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between">
                    <Typography
                        variant="body1"
                        fontSize={18}
                        fontWeight={theme.typography.fontWeightMedium}
                        component="div"
                    >
                        {page ?
                            <>
                                {t("settings.cms.pages.settings.dialog_title", "Page")} <b>{page.title}</b>
                            </>
                        :   t("settings.cms.pages.create_dialog.title", "Create page")}
                    </Typography>
                    {showStatusToggle && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={settingsActive}
                                    onChange={(e) => onChangeActive(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={
                                <FoodsTags
                                    label={settingsActive ? t("common.active", "Active") : t("common.draft", "Draft")}
                                    variant={settingsActive ? "tags-green" : "tags-red"}
                                />
                            }
                            sx={{ mt: 1 }}
                        />
                    )}
                </Stack>
            </DialogTitle>
            <DialogContent>
                <TextField
                    margin="dense"
                    id="title"
                    label={t("settings.cms.pages.table.title", "Title")}
                    type="text"
                    fullWidth
                    value={settingsTitle}
                    onChange={(e) => onChangeTitle(e.target.value)}
                    disabled={disabled}
                />

                {isPdfTemplate && (
                    <TextField
                        margin="dense"
                        id="description"
                        label={t("settings.cms.templates.description", "Description")}
                        type="text"
                        fullWidth
                        value={settingsDescription}
                        onChange={(e) => onChangeDescription(e.target.value)}
                        disabled={disabled}
                    />
                )}

                <TextField
                    margin="dense"
                    id="url"
                    label={t("settings.cms.pages.create_dialog.page_url", "URL")}
                    placeholder="/about, /pricing, /custom/path"
                    type="text"
                    fullWidth
                    value={settingsUrl}
                    onChange={(e) => onChangeUrl(e.target.value)}
                    error={urlError}
                    helperText={urlHelperText}
                    disabled={disabled}
                />

                <Autocomplete
                    {...cmsAutocompleteEnterSelectProps}
                    multiple
                    options={resolvedSectorOptions}
                    getOptionLabel={(option: CompanySector) => option.name}
                    value={resolvedSelectedSectorOptions}
                    onChange={(_, values) => onChangeSectorIds(values.map((v) => v.id))}
                    onInputChange={(_, value) => onSectorInputChange?.(value)}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t("settings.cms.pages.create_dialog.sector", "Sector (optional)")}
                            margin="dense"
                        />
                    )}
                    disabled={disabled}
                    sx={{ mt: 1 }}
                />

                <Autocomplete
                    {...cmsAutocompleteEnterSelectProps}
                    multiple
                    options={roleOptions}
                    value={settingsRoles}
                    onChange={(_, values) => onChangeRoles(values)}
                    getOptionLabel={getRoleDisplayName}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t("settings.cms.pages.table.roles", "Roles")}
                            margin="dense"
                            helperText={rolesLoading ? t("common.loading", "Loading") : undefined}
                        />
                    )}
                    disabled={disabled}
                    sx={{ mt: 1 }}
                />

                {isPdfTemplate && (
                    <>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                {t("settings.cms.templates.table.score", "Score")}
                            </Typography>
                            <Tooltip title={t("common.clear_all", "Clear all")}>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => onChangeScore?.([])}
                                        disabled={disabled || !hasScoreSelection}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                        <Autocomplete
                            {...cmsAutocompleteEnterSelectProps}
                            multiple
                            disableClearable
                            limitTags={1}
                            options={scoreOptions}
                            getOptionLabel={(o) => o.label}
                            isOptionEqualToValue={(o, v) => o.value === v.value}
                            value={selectedScoreOptions}
                            onChange={(_, values) => {
                                const next = values.map((v) => v.value);
                                if (next.includes("")) {
                                    onChangeScore?.([]);
                                    return;
                                }
                                onChangeScore?.(next);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={""}
                                    margin="dense"
                                    placeholder={hasScoreSelection ? "" : t("common.all", "All")}
                                />
                            )}
                            disabled={disabled}
                            sx={{ mt: 0 }}
                        />

                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                {t("settings.cms.templates.table.entity", "Entity")}
                            </Typography>
                            <Tooltip title={t("common.clear_all", "Clear all")}>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => onChangeEntity?.([])}
                                        disabled={disabled || !hasEntitySelection}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                        <Autocomplete
                            {...cmsAutocompleteEnterSelectProps}
                            multiple
                            disableClearable
                            limitTags={1}
                            options={entityOptions}
                            getOptionLabel={(o) => o.label}
                            isOptionEqualToValue={(o, v) => o.value === v.value}
                            value={selectedEntityOptions}
                            onChange={(_, values) => {
                                const next = values.map((v) => v.value);
                                if (next.includes("")) {
                                    onChangeEntity?.([]);
                                    return;
                                }
                                onChangeEntity?.(next);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={""}
                                    margin="dense"
                                    placeholder={hasEntitySelection ? "" : t("common.all", "All")}
                                />
                            )}
                            disabled={disabled}
                            sx={{ mt: 0 }}
                        />

                        <FormControl sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {t(
                                    "settings.cms.templates.download_only_on_ssq_validated",
                                    "Download only on SSQ validated",
                                )}
                            </Typography>
                            <RadioGroup
                                row
                                value={settingsDownloadOnlyOnSsqValidated ? "yes" : "no"}
                                onChange={(event) => {
                                    onChangeDownloadOnlyOnSsqValidated?.(event.target.value === "yes");
                                }}
                            >
                                <FormControlLabel
                                    value="yes"
                                    control={<Radio size="small" />}
                                    label={t("common.yes", "Yes")}
                                    disabled={disabled}
                                />
                                <FormControlLabel
                                    value="no"
                                    control={<Radio size="small" />}
                                    label={t("common.no", "No")}
                                    disabled={disabled}
                                />
                            </RadioGroup>
                        </FormControl>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="ternary" onClick={onClose} disabled={saving}>
                    {t("settings.cms.pages.dialog.action.cancel", "Cancel")}
                </Button>
                <Button onClick={onSave} variant="primary" disabled={saving || saveDisabled}>
                    {saving ?
                        <CircularProgress size={24} sx={{ color: "white" }} />
                    :   (saveButtonLabel ?? t("common.save", "Save"))}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PageSettingsDialog;
